import React, { useState } from 'react';
import styled from 'styled-components';
import FisioterapeutaSelector from '../components/FisioterapeutaSelector';
import HorarioSelector from '../components/HorarioSelector';
import FormularioPaciente from '../components/FormularioPaciente';
import PagoStripe from '../components/PagoStripe';
import { Fisioterapeuta, Paciente, Cita } from '../types';
import { crearCita } from '../services/googleCalendarService';
import { crearSesionPago } from '../services/stripeService';

enum Paso {
  SELECCIONAR_FISIOTERAPEUTA = 0,
  SELECCIONAR_HORARIO = 1,
  INGRESAR_DATOS = 2,
  REALIZAR_PAGO = 3,
  CONFIRMACION = 4,
}

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 15px 10px;
  }
  
  @media (max-width: 400px) {
    padding: 10px 5px;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 25px;
  background-color: #003366;
  padding: 20px 10px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 51, 102, 0.2);
  
  @media (max-width: 768px) {
    padding: 15px 10px;
    margin-bottom: 15px;
  }
  
  @media (max-width: 400px) {
    padding: 12px 8px;
    border-radius: 8px;
  }
`;

const Logo = styled.img`
  max-width: 150px;
  margin-bottom: 15px;
  
  @media (max-width: 480px) {
    max-width: 120px;
    margin-bottom: 10px;
  }
  
  @media (max-width: 400px) {
    max-width: 100px;
  }
`;

const Title = styled.h1`
  color: white;
  font-size: 2.2rem;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 8px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 400px) {
    font-size: 1.3rem;
  }
`;

const Subtitle = styled.p`
  color: #e6f2ff;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 400px) {
    font-size: 0.85rem;
  }
`;

const StepContainer = styled.div`
  margin-bottom: 30px;
  background-color: white;
  padding: 20px 15px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 15px 12px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 400px) {
    padding: 12px 8px;
    border-radius: 8px;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 35px;
  position: relative;
  padding: 0 5px;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 3px;
    background-color: #e0e0e0;
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 50px;
    overflow-x: auto;
    justify-content: flex-start;
    gap: 45px; /* Espacio mínimo entre bullets */
    padding-bottom: 15px;
    
    &::after {
      top: 40%;
    }
  }
  
  @media (max-width: 400px) {
    margin-bottom: 45px;
    gap: 40px;
  }
`;

interface StepBulletProps {
  active: boolean;
  completed: boolean;
}

const StepBullet = styled.div<StepBulletProps>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => 
    props.completed ? '#4caf50' : props.active ? '#0078d4' : '#e0e0e0'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  position: relative;
  z-index: 1;
  box-shadow: ${props => 
    props.active ? '0 4px 8px rgba(0, 120, 212, 0.3)' : 'none'};
  transition: all 0.3s ease;
  
  @media (max-width: 480px) {
    min-width: 40px; /* Asegurar tamaño mínimo en modo scroll */
  }
  
  @media (max-width: 400px) {
    width: 35px;
    height: 35px;
    min-width: 35px;
    font-size: 0.9rem;
  }
`;

const StepLabel = styled.div`
  position: absolute;
  top: 45px;
  text-align: center;
  width: 90px;
  font-size: 0.9rem;
  margin-left: -25px;
  color: #555;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    width: 80px;
    margin-left: -20px;
  }
  
  @media (max-width: 480px) {
    width: auto;
    white-space: nowrap;
    top: 50px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 400px) {
    top: 45px;
    font-size: 0.75rem;
    margin-left: -18px;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  gap: 15px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ primary?: boolean }>`
  background-color: ${props => props.primary ? '#0078d4' : '#f5f5f5'};
  color: ${props => props.primary ? 'white' : '#333'};
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  box-shadow: ${props => props.primary ? '0 4px 8px rgba(0, 120, 212, 0.2)' : 'none'};
  
  &:hover {
    background-color: ${props => props.primary ? '#005a9e' : '#e0e0e0'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 16px;
    
    &:last-child {
      order: -1; /* Poner el botón primario arriba en móviles */
    }
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  padding: 15px;
  background-color: #ffebee;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.95rem;
  border-left: 4px solid #d32f2f;
`;

const ConfirmationContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
  background-color: #f5f5f5;
  border-radius: 12px;
  margin-top: 30px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 30px 15px;
  }
`;

const ConfirmationIcon = styled.div`
  font-size: 4rem;
  color: #4caf50;
  margin-bottom: 20px;
  
  background-color: #e8f5e9;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
`;

const ConfirmationTitle = styled.h2`
  color: #4caf50;
  font-size: 2rem;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const ConfirmationText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 30px;
  color: #555;
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const ReservationDetails = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 12px;
  text-align: left;
  max-width: 600px;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 480px) {
    padding: 20px 15px;
  }
`;

const DetailRow = styled.div`
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 5px;
  }
`;

const DetailLabel = styled.div`
  font-weight: bold;
  flex: 1;
  color: #666;
`;

const DetailValue = styled.div`
  flex: 2;
  color: #333;
`;

const ReservaCita: React.FC = () => {
  const [pasoActual, setPasoActual] = useState<Paso>(Paso.SELECCIONAR_FISIOTERAPEUTA);
  const [fisioterapeutaSeleccionado, setFisioterapeutaSeleccionado] = useState<Fisioterapeuta | null>(null);
  const [fechaSeleccionada, setFechaSeleccionada] = useState<string | null>(null);
  const [horaSeleccionada, setHoraSeleccionada] = useState<string | null>(null);
  const [paciente, setPaciente] = useState<Paciente | null>(null);
  const [citaCreada, setCitaCreada] = useState<Cita | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

  const PRECIO_CITA = 40; // Precio en euros

  const pasos = [
    { label: 'Fisioterapeuta' },
    { label: 'Horario' },
    { label: 'Tus datos' },
    { label: 'Pago' },
    { label: 'Confirmación' },
  ];

  const avanzarPaso = () => {
    setPasoActual(prev => Math.min(prev + 1, Paso.CONFIRMACION) as Paso);
  };

  const retrocederPaso = () => {
    setPasoActual(prev => Math.max(prev - 1, Paso.SELECCIONAR_FISIOTERAPEUTA) as Paso);
  };

  const handleSelectFisioterapeuta = (fisio: Fisioterapeuta) => {
    setFisioterapeutaSeleccionado(fisio);
  };

  const handleSelectHorario = (fecha: string, hora: string) => {
    setFechaSeleccionada(fecha);
    setHoraSeleccionada(hora);
  };

  const handleSubmitDatosPaciente = async (datosPaciente: Paciente) => {
    if (!fisioterapeutaSeleccionado || !fechaSeleccionada || !horaSeleccionada) {
      setError('Falta información necesaria para crear la cita');
      return;
    }

    // Logs de depuración para fecha y hora seleccionadas
    console.log('Información de cita a crear:');
    console.log(`- Fisioterapeuta: ${fisioterapeutaSeleccionado.nombre} (ID: ${fisioterapeutaSeleccionado.id})`);
    console.log(`- Fecha seleccionada: ${fechaSeleccionada}`);
    console.log(`- Hora seleccionada: ${horaSeleccionada}`);
    console.log(`- Zona horaria del navegador: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
    console.log(`- Fecha actual navegador: ${new Date().toString()}`);
    
    setIsSubmitting(true);
    setPaciente(datosPaciente);

    try {
      // Preparar datos de la cita - NO crearemos la cita todavía, solo para referencia
      const dataCita = {
        fisioterapeutaId: fisioterapeutaSeleccionado.id,
        pacienteEmail: datosPaciente.email,
        fecha: fechaSeleccionada,
        hora: horaSeleccionada,
        duracion: 60, // 60 minutos
      };
      
      // PASO 1: Crear sólo la intención de pago (sin crear la cita aún)
      console.log('Creando intención de pago...');
      const resultado = await crearSesionPago(dataCita, datosPaciente);
      
      // Verificar que se haya creado correctamente la intención de pago
      if (!resultado) {
        throw new Error('No se pudo crear la intención de pago');
      }
      
      const { clientSecret, citaId } = resultado;
      setClientSecret(clientSecret);
      
      // Avanzar al paso de pago
      avanzarPaso();
    } catch (err) {
      console.error('Error en el proceso de creación de pago:', err);
      setError('Hubo un error al procesar tu solicitud. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentId: string) => {
    setIsSubmitting(true);
    setPaymentIntentId(paymentId);

    try {
      if (!fisioterapeutaSeleccionado || !fechaSeleccionada || !horaSeleccionada || !paciente) {
        throw new Error('Falta información necesaria para crear la cita');
      }
      
      // PASO 1: Ahora sí creamos la cita después de un pago exitoso
      console.log('Pago exitoso. Creando cita en el backend...');
      const dataCita = {
        fisioterapeutaId: fisioterapeutaSeleccionado.id,
        pacienteEmail: paciente.email,
        fecha: fechaSeleccionada,
        hora: horaSeleccionada,
        duracion: 60,
        estado: 'confirmada' as const,
        pagado: true,
        sesionStripeId: paymentId,
        // Incluimos el objeto paciente completo para el backend
        paciente: {
          nombre: paciente.nombre,
          apellidos: paciente.apellidos,
          email: paciente.email,
          telefono: paciente.telefono
        }
      };
      
      console.log('Datos completos de la cita a crear:', dataCita);
      
      // Crear la cita ya pagada
      const citaCreada = await crearCita(dataCita);
      
      if (!citaCreada || !citaCreada.id) {
        throw new Error('No se pudo crear la cita en el servidor');
      }
      
      console.log(`Cita creada con ID: ${citaCreada.id}`);
      setCitaCreada(citaCreada);
      
      // Avanzar al paso de confirmación
      avanzarPaso();
    } catch (err) {
      console.error('Error al crear la cita después del pago:', err);
      setError('Hubo un error al registrar tu cita. El pago se ha procesado, pero por favor contáctanos para confirmar tu reserva.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentCancel = () => {
    // Simplemente volver al paso anterior, no hay ninguna cita que eliminar
    retrocederPaso();
  };

  const renderPasoActual = () => {
    switch (pasoActual) {
      case Paso.SELECCIONAR_FISIOTERAPEUTA:
        return (
          <FisioterapeutaSelector
            onSelectFisioterapeuta={handleSelectFisioterapeuta}
            selectedFisioterapeutaId={fisioterapeutaSeleccionado?.id}
          />
        );
      case Paso.SELECCIONAR_HORARIO:
        return fisioterapeutaSeleccionado ? (
          <HorarioSelector
            fisioterapeutaId={fisioterapeutaSeleccionado.id}
            onSelectHorario={handleSelectHorario}
            fechaSeleccionada={fechaSeleccionada || undefined}
            horaSeleccionada={horaSeleccionada || undefined}
            nombreFisioterapeuta={fisioterapeutaSeleccionado.nombre}
          />
        ) : (
          <div>Por favor, selecciona primero un fisioterapeuta</div>
        );
      case Paso.INGRESAR_DATOS:
        return (
          <FormularioPaciente 
            onSubmit={handleSubmitDatosPaciente}
            isSubmitting={isSubmitting}
          />
        );
      case Paso.REALIZAR_PAGO:
        return clientSecret ? (
          <PagoStripe
            precio={PRECIO_CITA}
            clientSecret={clientSecret}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentCancel={handlePaymentCancel}
          />
        ) : (
          <div>Cargando información de pago...</div>
        );
      case Paso.CONFIRMACION:
        return (
          <ConfirmationContainer>
            <ConfirmationIcon>✓</ConfirmationIcon>
            <ConfirmationTitle>¡Reserva Confirmada!</ConfirmationTitle>
            <ConfirmationText>Tu cita ha sido reservada con éxito. Hemos enviado un correo electrónico con los detalles.</ConfirmationText>
            
            {citaCreada && fisioterapeutaSeleccionado && paciente && (
              <ReservationDetails>
                <DetailRow>
                  <DetailLabel>Fisioterapeuta:</DetailLabel>
                  <DetailValue>{fisioterapeutaSeleccionado.nombre}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Fecha:</DetailLabel>
                  <DetailValue>{fechaSeleccionada}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Hora:</DetailLabel>
                  <DetailValue>{horaSeleccionada}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Paciente:</DetailLabel>
                  <DetailValue>{paciente.nombre} {paciente.apellidos}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Email:</DetailLabel>
                  <DetailValue>{paciente.email}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Teléfono:</DetailLabel>
                  <DetailValue>{paciente.telefono}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Importe pagado:</DetailLabel>
                  <DetailValue>{PRECIO_CITA.toFixed(2)}€</DetailValue>
                </DetailRow>
              </ReservationDetails>
            )}
          </ConfirmationContainer>
        );
      default:
        return null;
    }
  };

  const puedeAvanzar = () => {
    switch (pasoActual) {
      case Paso.SELECCIONAR_FISIOTERAPEUTA:
        return !!fisioterapeutaSeleccionado;
      case Paso.SELECCIONAR_HORARIO:
        return !!fechaSeleccionada && !!horaSeleccionada;
      case Paso.INGRESAR_DATOS:
      case Paso.REALIZAR_PAGO:
      case Paso.CONFIRMACION:
        return false; // Estos pasos tienen su propia lógica de avance
      default:
        return false;
    }
  };

  return (
    <Container>
      <Header>
        <Logo src="/logo-fisiofine.png" alt="Fisiofine" />
        <Title>Reserva tu Cita</Title>
        <Subtitle>Selecciona fisioterapeuta y horario</Subtitle>
      </Header>

      <StepContainer>
        <StepIndicator>
          {pasos.map((paso, index) => (
            <div key={index} style={{ position: 'relative' }}>
              <StepBullet 
                active={index === pasoActual} 
                completed={index < pasoActual}
              >
                {index < pasoActual ? '✓' : index + 1}
              </StepBullet>
              <StepLabel>{paso.label}</StepLabel>
            </div>
          ))}
        </StepIndicator>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {renderPasoActual()}

        {pasoActual < Paso.INGRESAR_DATOS && (
          <NavigationButtons>
            <Button 
              onClick={retrocederPaso} 
              disabled={pasoActual === Paso.SELECCIONAR_FISIOTERAPEUTA}
            >
              Anterior
            </Button>
            <Button 
              primary 
              onClick={avanzarPaso}
              disabled={!puedeAvanzar()}
            >
              Siguiente
            </Button>
          </NavigationButtons>
        )}
      </StepContainer>
    </Container>
  );
};

export default ReservaCita; 